import React from "react";
import styled from "styled-components";
import {
  Image,
  ImageProps,
  Theme,
  colors,
  font,
  media,
} from "@literati/public-ui-shared-react";
import { SubscriptionPlanCamelCase } from "lib/api/subscription";
import { CreditInfoBanner } from "components/PlanSelection/PlanSelectionRadio";
import CheckoutDonationPromo from "components/checkout/CheckoutDonationPromo";
import { NormalizedOrderSummary } from "components/Step/stepTypes";
import PaymentOrderSummary from "components/Payment/PaymentOrderSummary";
import { PaymentParagraph } from "components/Payment/PaymentPrimitives";
import { isCouponClaimedByQuery } from "components/Step/stepUtil";
import { FALL25_COUPON_CODE } from "components/Step/stepUtil";
import { useRouter } from "next/router";
import MembershipFall25OfferBadge from "components/Membership/MembershipFall25OfferBadge";
import { FEATURE_FLAG_SHOW_FALL25_COUPON } from "components/Membership/membershipFeatureFlags";
import { isTbybMonthly2025OfferPlan } from "components/PlanSelection/planSelectionUtil";

const CLASS_BASE = "payment-section-order-details";
export const PaymentSectionOrderDetailsClassName = {
  BASE: CLASS_BASE,
} as const;

const StyledH2 = styled.h2`
  font-size: 1rem;
  font-weight: 500;
`;

const BoxPriceContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 1.25rem 0 0;
`;

const StyledP = styled.p`
  flex-basis: 50%;
  flex-shrink: 0;
  text-align: center;

  ${media.lg} {
    font-size: 1.5rem;
  }
`;

const StruckPrice = styled.span`
  text-decoration: line-through;
  font-size: 1rem;
`;

const NewPrice = styled.span`
  font-size: 1.4rem;
  padding: 0 0 0 0.125rem;
`;

const PerMonth = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
`;

const StyledCreditInfoBanner = styled(CreditInfoBanner)`
  margin: 1rem 0 0;
  font-weight: ${font.weight.roman};
  cursor: pointer;
`;

const StyledDiv = styled.div`
  min-width: 140px;
  flex-shrink: 0;
`;

const OfferBadgeWrapper = styled.div`
  display: inline-flex;
  margin-bottom: 0.25rem;
`;

const AnnualP = styled.p`
  font-size: 0.8125rem;
`;

const PromoDetails = styled.p`
  margin-top: 0.25rem;
  font-size: 0.75rem;
  text-align: left;
`;

const Fall25PromoPrice = styled(StyledP)`
  text-align: left;
`;

export interface PaymentSectionOrderDetailsProps {
  boxImageProps?: ImageProps | null;
  giftCode?: string;
  orderSummary?: NormalizedOrderSummary | null;
  plan?: SubscriptionPlanCamelCase | null;
  suppressCouponForm?: boolean;
}

export const PaymentSectionOrderDetails = ({
  boxImageProps,
  giftCode,
  orderSummary,
  plan,
  suppressCouponForm,
}: PaymentSectionOrderDetailsProps) => {
  const router = useRouter();
  const shouldShowFall25OfferBadge =
    FEATURE_FLAG_SHOW_FALL25_COUPON &&
    isCouponClaimedByQuery(router.query, FALL25_COUPON_CODE);

  const isAnnual = plan?.boxCount === 12;
  const isPromoApplied = !!orderSummary?.coupon;
  const isFall25MonthlyOfferUi = shouldShowFall25OfferBadge && isPromoApplied;
  const showAnnualBillingDisclaimers =
    isAnnual && !isTbybMonthly2025OfferPlan(plan) && !isFall25MonthlyOfferUi;

  const promoAmount = orderSummary?.coupon?.value;
  const promoAmountNumber = Number(promoAmount?.replace(/[^0-9.-]+/g, ""));

  const subtotal = orderSummary?.subtotal?.value;
  const subTotalNumber = Number(subtotal?.replace(/[^0-9.-]+/g, ""));

  const totalAfterCoupon = `$${(subTotalNumber + promoAmountNumber).toFixed(
    2
  )}`;

  return (
    <Theme>
      <>
        <StyledH2>Your Reader’s Membership</StyledH2>
        <BoxPriceContainer>
          {boxImageProps ? (
            <Image
              css="flex-basis: 50%"
              alt="book box"
              targetHeight={120}
              {...boxImageProps}
            />
          ) : null}
          {plan ? (
            <StyledDiv>
              {shouldShowFall25OfferBadge ? (
                <OfferBadgeWrapper>
                  <MembershipFall25OfferBadge
                    backgroundColor="transparent"
                    textColor={colors.literatiTeal}
                    iconColor={colors.literatiTeal}
                  />
                </OfferBadgeWrapper>
              ) : null}
              {isPromoApplied ? (
                shouldShowFall25OfferBadge ? (
                  <>
                    <Fall25PromoPrice>
                      <NewPrice>{totalAfterCoupon}</NewPrice>
                      <PerMonth> / mo</PerMonth>
                    </Fall25PromoPrice>
                    <PromoDetails>
                      Monthly fee {subtotal}
                      <br />
                      after 3 months.
                    </PromoDetails>
                  </>
                ) : (
                  <StyledP>
                    <StruckPrice>{subtotal} </StruckPrice>
                    <NewPrice>{totalAfterCoupon}</NewPrice>
                    <PerMonth> per month</PerMonth>
                  </StyledP>
                )
              ) : (
                <StyledP>{plan.costPerBox} / month</StyledP>
              )}
              {showAnnualBillingDisclaimers ? (
                <AnnualP>(paid yearly at {plan.amount})</AnnualP>
              ) : null}
            </StyledDiv>
          ) : null}
        </BoxPriceContainer>
        <PaymentParagraph css="margin: 1.25rem 0 0">
          Borrow or buy. Book prices shown in box. Prices beat Amazon. Check out
          or return box to get a new one.
        </PaymentParagraph>
        {showAnnualBillingDisclaimers ? <StyledCreditInfoBanner /> : null}
        <CheckoutDonationPromo css="margin: 1rem 0 0" />
        <PaymentOrderSummary
          giftCode={giftCode}
          orderSummary={orderSummary}
          suppressCouponForm={suppressCouponForm}
        />
      </>
    </Theme>
  );
};

export default PaymentSectionOrderDetails;
